import { explodeCanvasEl } from "./elements";
import anime from "animejs/lib/anime.es.js";

const c = document.getElementById("explode");
const ctx = c.getContext("2d");
let cH;
let cW;
const animations = [];

class ColorPicker {
  constructor() {
    this.colors = ["#FF6138", "#FFBE53", "#2980B9", "#282741"];
    this.index = 0;
  }

  next() {
    this.index = this.index + 1 < this.colors.length ? this.index + 1 : 0;
    return this.colors[this.index];
  }

  current() {
    return this.colors[this.index];
  }
}

const colorPicker = new ColorPicker();

function removeAnimation(animation, resolve = null) {
  const index = animations.indexOf(animation);
  if (index > -1) animations.splice(index, 1);
  if (resolve) {
    ctx.clearRect(0, 0, c.width, c.height);
    resolve();
  }
}

function calcPageFillRadius(x, y) {
  const l = Math.max(x - 0, cW - x);
  const h = Math.max(y - 0, cH - y);
  return Math.sqrt(Math.pow(l, 2) + Math.pow(h, 2));
}

class Circle {
  constructor(opts) {
    Object.assign(this, opts);
  }

  draw() {
    if (explodeCanvasEl.style.display === "none") return;
    ctx.globalAlpha = this.opacity || 1;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.r, 0, 2 * Math.PI);
    if (this.stroke) {
      ctx.strokeStyle = this.stroke.color;
      ctx.lineWidth = this.stroke.width;
      ctx.stroke();
    }
    if (this.fill) {
      ctx.fillStyle = this.fill;
      ctx.fill();
    }
    ctx.closePath();
    ctx.globalAlpha = 1;
  }
}

export class Exploder {
  constructor(x, y, speed = 1) {
    this.x = x;
    this.y = y;
    this.speed = speed;
  }

  _explode = (resolve) => {
    const currentColor = colorPicker.current();
    const nextColor = colorPicker.next();
    const targetR = calcPageFillRadius(this.x, this.y);
    const rippleSize = Math.min(200, cW * 0.4);
    const minCoverDuration = 750 / this.speed;

    const pageFill = new Circle({
      x: this.x,
      y: this.y,
      r: 0,
      fill: nextColor,
    });
    const fillAnimation = anime({
      targets: pageFill,
      r: targetR,
      duration: Math.max(targetR / 2 / this.speed, minCoverDuration),
      easing: "easeOutQuart",
      complete: function () {
        removeAnimation(fillAnimation);
      },
    });

    const ripple = new Circle({
      x: this.x,
      y: this.y,
      r: 0,
      fill: currentColor,
      stroke: {
        width: 3,
        color: currentColor,
      },
      opacity: 1,
    });
    const rippleAnimation = anime({
      targets: ripple,
      r: rippleSize,
      opacity: 0,
      easing: "easeOutExpo",
      duration: 900 / this.speed,
      complete: removeAnimation,
    });

    const particles = [];
    for (let i = 0; i < 32; i++) {
      const particle = new Circle({
        x: this.x,
        y: this.y,
        fill: currentColor,
        r: anime.random(24, 48),
      });
      particles.push(particle);
    }
    const particlesAnimation = anime({
      targets: particles,
      x: function (particle) {
        return particle.x + anime.random(rippleSize, -rippleSize);
      },
      y: function (particle) {
        return particle.y + anime.random(rippleSize * 1.15, -rippleSize * 1.15);
      },
      r: 0,
      easing: "easeOutExpo",
      duration: anime.random(600, 900) / this.speed,
      complete: () => removeAnimation(null, resolve),
    });
    animations.push(fillAnimation, rippleAnimation, particlesAnimation);
  };

  explode(x, y, speed = 1) {
    if (x !== undefined && y !== undefined) {
      this.x = x;
      this.y = y;
      this.speed = speed;
    }
    return new Promise(this._explode);
  }
}

window.animate = anime({
  duration: Infinity,
  update: () => {
    animations.forEach((anim) => {
      anim.animatables.forEach((animatable) => animatable.target.draw());
    });
  },
});

const resizeCanvas = function () {
  cW = window.innerWidth;
  cH = window.innerHeight;
  c.width = cW * devicePixelRatio;
  c.height = cH * devicePixelRatio;
  ctx.scale(devicePixelRatio, devicePixelRatio);
};

export const initCanvas = function () {
  resizeCanvas();
  window.addEventListener("resize", resizeCanvas);
};

export async function explodeWithCanvas(exploder, ...args) {
  explodeCanvasEl.style.display = "initial";
  await exploder.explode(...args);
  explodeCanvasEl.style.display = "none";
}
